var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ModuleId, ButtonType, StartRoomTransitionMessage, ChangeInputDeviceButtonMappingMessage, DisableGestureMessage, DisableAllGesturesMessage, GestureType, RoomChangedMessage, degToRad, SetEnabledGesturesMessage, ControllerAxisMessage, AxisType, ControllerHand } from "flavr-common";
import { Vector3 } from "flavr-math";
import { animationFrame$ } from "@messages/xr/AnimationFrame";
import { MovementMode, RequestCamRotationActiveMessage, RequestMovementSettingsMessage, ResponseMovementSettingsMessage } from "@messages/movement/MovementMessages";
import { WebXRPresentingMessage } from "@messages/xr/WebXRMessages";
import { BUTTON_MAPPING } from "@modules/inputs/devices/InputMapping";
import { BUTTON_PRESSED_VALUE_THRESHOLD } from "@modules/inputs/controller/ControllerInputDevice";
import { localPlayerContainer } from "@modules/localplayer/LocalPlayerContainer";
import { preferencesService } from "@modules/preferences/PreferencesService";
import { xrControllerService } from "@modules/xr/XRControllerService";
import { Module } from "@modules/module";
import { renderManager } from "@modules/xr/RenderManager";
import { clockService } from "@modules/xr/ClockService";
import { inputService } from "@modules/inputs/input/InputService";
import { modelPool } from "@modules/model";
import { navMeshService } from "@modules/navMesh";
import { ComputeAngle } from "./nodes/Compute";
import { GestureEnabledCondition, FindNextControllerWithButtonCondition, AngleCondition } from "./nodes/Condition";
import { TwoHandedGestureView } from "./TwoHandedGestureView";
import { GrabRotateGesture } from "./GrabRotateGesture";
import { GrabScaleGesture } from "./GrabScaleGesture";
import { GrabMoveGesture } from "./GrabMoveGesture";
import { Selector, GestureNode } from "./nodes/Node";
import { annotationService } from "./AnnotationService";
// Don't strain the garbage-collector!
const tmpMoveVector = new Vector3();
const tmpRotateVector = new Vector3();
/**
 * Recognizes controller-based movement and manipulation gestures and turns raw input into higher-level locomotion and interaction behaviour.
 *
 * Messages:
 * - `RequestMovementSettingsMessage` and `ResponseMovementSettingsMessage` - Expose the current locomotion settings.
 * - `RequestCamRotationActiveMessage` - Enables or disables camera rotation while other features temporarily take control.
 * - `ControllerAxisMessage` - Provides the controller axis input that drives locomotion and gesture state.
 * - `WebXRPresentingMessage` - Signals XR presentation mode changes that affect gesture behaviour.
 * - `StartRoomTransitionMessage`, `RoomChangedMessage`, `SetEnabledGesturesMessage`, `DisableGestureMessage`, `DisableAllGesturesMessage` and `ChangeInputDeviceButtonMappingMessage` - Reconfigure gesture processing during room and input changes.
 */
export class GestureModule extends Module {
    /**
     * Subscribes to movement/gesture-related messages, wires up controller disconnect handling, and restores
     * movement-related user preferences (VR movement mode, movement sensitivity, dead zones, grab/analog-stick toggles)
     * from the preferences service, keeping them in sync with subsequent preference changes.
     */
    constructor() {
        var _a, _b, _c;
        super(ModuleId.GESTURE);
        this.gestures = new Map();
        this.camRotationDeactivations = new Set();
        this.disabled = false;
        this.roomTransitionInProgress = false;
        this.grabButtons = {};
        this.gestureView = new TwoHandedGestureView();
        this.currentRoomIsRoot = true;
        this.currData = {};
        this.prevData = {};
        this.headHeight = 1.7;
        this.canFly = false;
        this.movementVectorLeftController = new Vector3();
        this.movementVectorRightController = new Vector3();
        this.rotationValue = 0;
        this.movementSensitivityVR = 1;
        // Regular (non-VR): last non-zero input delta is re-applied each frame until input returns to 0 or changes
        this.residualRotationDelta = new Vector3();
        this.analogStickDeadZoneThreshold = 0;
        this.vrMovementMode = "walk+teleport";
        this.disableGrabMovement = false;
        this.disableGrabRotation = false;
        this.disableAnalogStickMovement = false;
        this.handleRequestCamRotationActiveMessage = (msg, meta) => {
            if (!msg.isActive) {
                this.camRotationDeactivations.add(`${meta.source.moduleId}|${meta.source.workerId}`);
                localPlayerContainer.firstPersonControls.activeLook = msg.isActive;
            }
            else {
                this.camRotationDeactivations.delete(`${meta.source.moduleId}|${meta.source.workerId}`);
                if (this.camRotationDeactivations.size === 0) {
                    localPlayerContainer.firstPersonControls.activeLook = msg.isActive;
                }
            }
        };
        this.handleControllerAxisMessage = (msg) => {
            const controllerData = xrControllerService.getControllerData(msg.uid);
            if (controllerData === undefined)
                return;
            // Ignore movement input when in pure teleport mode or when analog stick movement is disabled
            if (this.vrMovementMode === "teleport" || this.disableAnalogStickMovement)
                return;
            let inputValueAboveThreshold = false;
            // ensure that the analog stick is not in the dead zone
            if (this.analogStickDeadZoneThreshold !== undefined && Math.abs(msg.value) > this.analogStickDeadZoneThreshold) {
                inputValueAboveThreshold = true;
            }
            if (controllerData.hand === ControllerHand.LeftHand) {
                // Left Controller: MoveFormward and Rotate
                if (msg.axisType === AxisType.THUMBSTICK_X || msg.axisType === AxisType.TOUCHPAD_X) {
                    this.rotationValue = inputValueAboveThreshold ? msg.value : 0;
                }
                else if (msg.axisType === AxisType.THUMBSTICK_Y || msg.axisType === AxisType.TOUCHPAD_Y) {
                    this.movementVectorLeftController.set(0, 0, inputValueAboveThreshold ? msg.value : 0);
                }
            }
            if (controllerData.hand === ControllerHand.RightHand) {
                // Right Controller: MoveForward and MoveSidewards
                if (msg.axisType === AxisType.THUMBSTICK_X || msg.axisType === AxisType.TOUCHPAD_X) {
                    this.movementVectorRightController.x = inputValueAboveThreshold ? msg.value : 0;
                }
                else if (msg.axisType === AxisType.THUMBSTICK_Y || msg.axisType === AxisType.TOUCHPAD_Y) {
                    this.movementVectorRightController.z = inputValueAboveThreshold ? msg.value : 0;
                }
            }
        };
        // Reset movement and rotation values when switching mode
        this.handleWebXRPresentingMessage = (msg) => {
            if (msg.isPresenting) {
                this.movementVectorLeftController.set(0, 0, 0);
                this.movementVectorRightController.set(0, 0, 0);
                this.rotationValue = 0;
            }
        };
        this.updateGestures = () => {
            if (this.disabled || this.roomTransitionInProgress) {
                localPlayerContainer.update();
                return;
            }
            if (this.rootNode !== undefined) {
                this.prevData = this.currData;
                this.currData = {};
                this.rootNode.canExecute(this);
                this.rootNode.execute(this);
                const newGesture = this.rootNode.findGesture(this);
                if (newGesture !== this.activeGesture) {
                    if (this.activeGesture !== undefined) {
                        this.activeGesture.end();
                    }
                    this.activeGesture = newGesture;
                    if (this.activeGesture !== undefined) {
                        this.activeGesture.begin();
                    }
                }
                if (this.activeGesture !== undefined) {
                    const messages = [];
                    this.activeGesture.update(messages);
                    if (messages.length > 0)
                        this.connection.sendMultiple(messages);
                }
            }
            localPlayerContainer.update();
            inputService.update();
            this.updateMovement();
            localPlayerContainer.updateTransform();
            inputService.onAfterInput();
        };
        this.handleRequestMovementSettingsMessage = (msg, askId) => {
            if (msg.params.movementMode !== undefined) {
                this.changeMovement(msg.params.movementMode);
                this.updateMovementSettings();
            }
            if (msg.params.canFly !== undefined) {
                this.canFly = msg.params.canFly;
                this.updateMovementSettings();
            }
            if (msg.params.headHeight !== undefined) {
                this.headHeight = msg.params.headHeight;
                this.updateMovementSettings();
            }
            if (msg.params.navMashLayerId !== undefined) {
                navMeshService.changeCurrentLayerId(msg.params.navMashLayerId);
                this.updateMovementSettings();
            }
            if (askId !== undefined) {
                this.connection.answer(askId, new ResponseMovementSettingsMessage(navMeshService._currentLocalLayerId, localPlayerContainer.movementMode, this.canFly, this.headHeight));
            }
        };
        this.onNavMeshShouldRecompute = () => {
            localPlayerContainer.firstPersonControls.shouldRecomputeNavGroupAndNavNode = true;
            localPlayerContainer.firstPersonControls.update(0);
        };
        this.connection.subscribeByTypeOrAskRequest(RequestMovementSettingsMessage, this.handleRequestMovementSettingsMessage);
        this.connection.subscribeByType(RequestCamRotationActiveMessage, this.handleRequestCamRotationActiveMessage);
        this.connection.subscribeByType(ControllerAxisMessage, this.handleControllerAxisMessage);
        this.connection.subscribeByType(WebXRPresentingMessage, this.handleWebXRPresentingMessage);
        this.connection.subscribe([
            StartRoomTransitionMessage.typestatic,
            ChangeInputDeviceButtonMappingMessage.typestatic,
            SetEnabledGesturesMessage.typestatic,
            DisableGestureMessage.typestatic,
            DisableAllGesturesMessage.typestatic,
            RoomChangedMessage.typestatic
        ], (msg) => {
            this.handleMessage(msg);
        });
        this.initButtonMapping();
        navMeshService.onShouldRecomputeDelegate.addSafe(this.onNavMeshShouldRecompute);
        xrControllerService.onDisconnectDelegate.add((data) => {
            if (data.hand === ControllerHand.LeftHand) {
                this.movementVectorLeftController.set(0, 0, 0);
                this.rotationValue = 0;
            }
            else {
                this.movementVectorRightController.set(0, 0, 0);
            }
        });
        annotationService.initialize();
        this.movementSensitivityVR = preferencesService.getNumberPreferredValue("movementSensitivityVR", 1);
        preferencesService.onPreferenceChanged.add((key, newValue) => __awaiter(this, void 0, void 0, function* () {
            if (key !== "movementSensitivityVR")
                return false;
            this.movementSensitivityVR = parseFloat(newValue);
            return true;
        }));
        this.analogStickDeadZoneThreshold = preferencesService.getNumberPreferredValue("analogStickDeadZoneThreshold", 0);
        preferencesService.onPreferenceChanged.add((key, newValue) => __awaiter(this, void 0, void 0, function* () {
            if (key !== "analogStickDeadZoneThreshold")
                return false;
            this.analogStickDeadZoneThreshold = parseFloat(newValue);
            return true;
        }));
        this.disableGrabMovement = (_a = preferencesService.getBooleanPreferredValue("disableGrabMovement", false)) !== null && _a !== void 0 ? _a : false;
        preferencesService.onPreferenceChanged.add((key, newValue) => __awaiter(this, void 0, void 0, function* () {
            if (key !== "disableGrabMovement")
                return false;
            this.disableGrabMovement = !!newValue;
            this.applyGrabMovementPreference();
            return true;
        }));
        this.disableGrabRotation = (_b = preferencesService.getBooleanPreferredValue("disableGrabRotation", false)) !== null && _b !== void 0 ? _b : false;
        preferencesService.onPreferenceChanged.add((key, newValue) => __awaiter(this, void 0, void 0, function* () {
            if (key !== "disableGrabRotation")
                return false;
            this.disableGrabRotation = !!newValue;
            this.disableGesture(GestureType.GrabRotate, this.disableGrabRotation);
            return true;
        }));
        this.disableAnalogStickMovement = (_c = preferencesService.getBooleanPreferredValue("disableAnalogStickMovement", false)) !== null && _c !== void 0 ? _c : false;
        preferencesService.onPreferenceChanged.add((key, newValue) => __awaiter(this, void 0, void 0, function* () {
            if (key !== "disableAnalogStickMovement")
                return false;
            this.disableAnalogStickMovement = !!newValue;
            if (!this.disableAnalogStickMovement) {
                this.movementVectorLeftController.set(0, 0, 0);
                this.movementVectorRightController.set(0, 0, 0);
                this.rotationValue = 0;
            }
            return true;
        }));
    }
    /**
     * Builds the gesture graph, restores preference-driven movement settings and registers the per-frame update callback that evaluates active gestures.
     */
    init() {
        this.gestureView.init(this.connection);
        this.vrMovementMode = preferencesService.getPreferredValue("vrMovementMode", "walk+teleport");
        preferencesService.onPreferenceChanged.add((preferenceName, newValue) => __awaiter(this, void 0, void 0, function* () {
            if (preferenceName === "vrMovementMode") {
                this.vrMovementMode = newValue;
            }
            return true;
        }));
        const rotateGesture = new GrabRotateGesture(this, this.gestureView);
        const scaleGesture = new GrabScaleGesture(this, this.gestureView);
        const move2DGesture = new GrabMoveGesture(false, this);
        const move3DGesture = new GrabMoveGesture(true, this);
        this.addGesture(rotateGesture);
        this.addGesture(scaleGesture);
        this.addGesture(move2DGesture);
        this.addGesture(move3DGesture);
        const startNode = new Selector();
        this.rootNode = startNode;
        startNode.add(new Selector()
            .addCondition(new FindNextControllerWithButtonCondition())
            .add(new Selector()
            .addCondition(new FindNextControllerWithButtonCondition())
            .add(new GestureNode(rotateGesture)
            .addCompute(new ComputeAngle())
            .addCondition(new GestureEnabledCondition(GestureType.GrabScale, false))
            .addCondition(new AngleCondition(degToRad(15))))
            .add(new GestureNode(scaleGesture)))
            .add(new GestureNode(move2DGesture))
            .add(new GestureNode(move3DGesture)));
        this.disableGesture(GestureType.GrabMove2D, true);
        this.disableGesture(GestureType.GrabScale, true);
        this.applyGrabMovementPreference();
        this.disableGesture(GestureType.GrabRotate, this.disableGrabRotation);
        animationFrame$.onMovementUpdate$.add(this.updateGestures);
    }
    handleMessage(msg) {
        if (StartRoomTransitionMessage.is(msg)) {
            this.roomTransitionInProgress = true;
            this.resetActiveGesture();
        }
        else if (ChangeInputDeviceButtonMappingMessage.is(msg)) {
            const buttonIndex = parseInt(msg.buttonID);
            if (msg.buttonType === ButtonType.GRAB) {
                this.grabButtons[msg.device].push(buttonIndex);
            }
            else {
                this.grabButtons[msg.device] = this.grabButtons[msg.device].filter((i) => i !== buttonIndex);
            }
        }
        else if (DisableGestureMessage.is(msg)) {
            this.disableGesture(msg.gesture, msg.disable);
        }
        else if (DisableAllGesturesMessage.is(msg)) {
            this.resetActiveGesture();
            this.disabled = msg.disable;
        }
        else if (RoomChangedMessage.is(msg)) {
            this.resetActiveGesture();
            this.roomTransitionInProgress = false;
            const roomModel = modelPool.getModel(msg.uid);
            this.currentRoomIsRoot = roomModel !== undefined ? roomModel.getRoomDepth() < 1 : true;
        }
        else if (SetEnabledGesturesMessage.is(msg)) {
            const enabledGestures = new Set(msg.enabledGestures);
            for (const [type, gesture] of this.gestures) {
                const enabled = enabledGestures.has(type);
                if (enabled === gesture.enabled)
                    continue;
                gesture.enabled = enabled;
                if (!enabled && this.activeGesture === gesture) {
                    this.resetActiveGesture();
                }
            }
        }
    }
    /**
     * Exposes the module bus connection so gesture helpers can send and receive messages through the same channel.
     */
    getConnection() {
        return this.connection;
    }
    resetActiveGesture() {
        if (this.activeGesture !== undefined) {
            this.activeGesture.end();
            this.activeGesture = undefined;
        }
    }
    disableGesture(gestureType, disable) {
        const gesture = this.gestures.get(gestureType);
        if (gesture !== undefined) {
            gesture.enabled = !disable;
            if (disable && this.activeGesture === gesture) {
                this.resetActiveGesture();
            }
        }
    }
    applyGrabMovementPreference() {
        this.disableGesture(GestureType.GrabMove3D, this.disableGrabMovement);
    }
    initButtonMapping() {
        for (const deviceType in BUTTON_MAPPING) {
            const grabButtons = (this.grabButtons[deviceType] = []);
            const mapping = BUTTON_MAPPING[deviceType];
            for (const buttonId in mapping) {
                if (mapping[buttonId] === ButtonType.GRAB) {
                    const buttonIndex = parseInt(buttonId);
                    if (!isNaN(buttonIndex))
                        grabButtons.push(buttonIndex);
                }
            }
        }
    }
    /**
     * Registers a gesture instance under its type and returns it so callers can chain additional gesture setup.
     */
    addGesture(gesture) {
        this.gestures.set(gesture.type, gesture);
        return gesture;
    }
    /**
     * Returns the registered gesture for the requested type if that gesture is available in the current gesture set.
     */
    getGesture(type) {
        return this.gestures.get(type);
    }
    /**
     * Reports whether the gesture identified by the given type is currently enabled.
     */
    isGestureEnabled(type) {
        const gesture = this.getGesture(type);
        return (gesture === null || gesture === void 0 ? void 0 : gesture.enabled) === true;
    }
    updateMovement() {
        const deltaTime = clockService.getDeltaToLastFrameInSeconds();
        if (!renderManager.isXRPresenting()) {
            // process movement from keyboard / mouse
            const moveInput = tmpMoveVector.set(inputService.getAxisValue("moveRight"), inputService.getAxisValue("moveUp"), inputService.getAxisValue("moveForward"));
            const rotateInput = tmpRotateVector.set(inputService.getAxisValue("lookUp"), inputService.getAxisValue("turn"), 0);
            // Exponential smoothing: lerp residual toward current input each frame.
            // smoothFactor is frame-rate independent: ~15 convergences per second → responsive but smooth.
            const smoothFactor = Math.min(deltaTime * 15, 1);
            // On direction reversal, snap residual to zero so the new direction is felt immediately.
            if (rotateInput.x !== 0 && this.residualRotationDelta.x !== 0 && Math.sign(rotateInput.x) !== Math.sign(this.residualRotationDelta.x)) {
                this.residualRotationDelta.x = 0;
            }
            if (rotateInput.y !== 0 && this.residualRotationDelta.y !== 0 && Math.sign(rotateInput.y) !== Math.sign(this.residualRotationDelta.y)) {
                this.residualRotationDelta.y = 0;
            }
            this.residualRotationDelta.x += (rotateInput.x - this.residualRotationDelta.x) * smoothFactor;
            this.residualRotationDelta.y += (rotateInput.y - this.residualRotationDelta.y) * smoothFactor;
            localPlayerContainer.firstPersonControls.rotateDelta.set(this.residualRotationDelta.x, this.residualRotationDelta.y, 0);
            localPlayerContainer.firstPersonControls.moveDelta.add(moveInput);
            localPlayerContainer.firstPersonControls.update(deltaTime);
        }
        else {
            // vr movement
            // enable keyboard movement in VR as well
            // process movement from controllers
            const movementSensitivity = (this.movementSensitivityVR !== undefined) ? this.movementSensitivityVR : 1;
            // Preserve the former 0.05-per-frame speed at 60 Hz.
            const movementStep = 3 * deltaTime * movementSensitivity;
            const moveVector = tmpMoveVector.set(inputService.getAxisValue("moveRight"), inputService.getAxisValue("moveUp"), -1 * inputService.getAxisValue("moveForward"))
                .add(this.movementVectorLeftController)
                .add(this.movementVectorRightController)
                .multiplyScalar(movementStep);
            const rotateValue = (-this.rotationValue + inputService.getAxisValue("turn")) * movementStep;
            localPlayerContainer.moveAlongAxes(moveVector.x, moveVector.y, moveVector.z);
            localPlayerContainer.rotatePlatform(rotateValue, true);
        }
    }
    /**
     * Checks the configured grab inputs on the supplied controller to determine whether grab-based gestures may start or continue.
     */
    isGrabButtonPressed(info) {
        const grabButtons = this.grabButtons[info.type];
        if (grabButtons !== undefined) {
            const buttons = info.gamepad.buttons;
            for (const buttonIndex of grabButtons) {
                if (buttonIndex >= 0 && buttonIndex < buttons.length) {
                    if (buttons[buttonIndex].value >= 1 - BUTTON_PRESSED_VALUE_THRESHOLD)
                        return true;
                }
            }
        }
        return false;
    }
    updateMovementSettings() {
        switch (localPlayerContainer.movementMode) {
            case MovementMode.FirstPerson:
                localPlayerContainer.firstPersonControls.changeNavMeshLayerId();
                localPlayerContainer.firstPersonControls.canFly = this.canFly;
                localPlayerContainer.firstPersonControls.headHeight = this.headHeight;
                break;
        }
    }
    changeMovement(mode) {
        localPlayerContainer.changeMovementMode(mode);
    }
}
